import React from "react";
import {
  WcButton,
  WcStack,
  WcTypography,
  WcLink,
  Box,
  useTheme,
  WcPaper,
  WcMaskedTextfield,
  WcLinearProgress,
} from "@bpipartners/webui";
import { useTranslations } from "next-intl";
import { LoginForm, LoginFormState } from "../../forms/login";
import axios from "axios";
import { TenantAutoComplete } from "@guberno/composite/tenant";
import { TenantAllUserTenant } from "@guberno/data-access";
import { AppContext } from "@guberno/utils/context";

interface OtpState {
  code: number;
  validLength: boolean;
  failedOtp: boolean;
}
export const LoginComposite = () => {
  const t = useTranslations();
  const loginRef = React.useRef();
  const theme = useTheme();
  const { localAppName } = React.useContext(AppContext);
  const isCapensis = localAppName === "capensis";
  const [failedLogin, setFailedLogin] = React.useState<boolean>(false);
  const [multipleTenants, setMultipleTenants] = React.useState<boolean>(false);
  const [tenant, setTenant] = React.useState("");
  const [mfa, setMfa] = React.useState<boolean>(false);
  const [otp, setOtp] = React.useState<OtpState>({
    code: undefined,
    validLength: false,
    failedOtp: false,
  });
  const [tenantList, setTenantList] = React.useState<TenantAllUserTenant[]>([]);
  const handleLoginDetails = (val: LoginFormState) => {
    if (val) {
      axios
        .post(
          "/auth/login",
          { username: val.userName, password: val.password },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            withCredentials: true,
          }
        )
        .then((res: any) => {
          if (res && res.data && res.data.Status === 200) {
            setFailedLogin(false);

            if (res.data && res.data.multipleTenants) {
              setTenant(res.data.tenantID);

              setTenantList(res.data.tenants);
              setMultipleTenants(true);
            } else {
              const localTenant = res.data.tenants[0];

              if (localTenant.mfaEnabled) {
                setMfa(true);
                //@ts-ignore
                setTenant(val);
              } else {
                window.location.href = "/";
              }
            }
          } else {
            setFailedLogin(true);
          }
        })
        .catch((FEerror: any) => {
          console.log("FE error", FEerror);
          setFailedLogin(true);
        });
    }
  };

  const handleLoginClicked = () => {
    if (loginRef && loginRef.current) {
      //@ts-ignore
      loginRef.current.onSubmit();
    }
  };

  const handleTenantSelected = (val: TenantAllUserTenant) => {
    console.log("Tenant selected", val);
    if (val) {
      axios
        .post(
          "/auth/switchTenant",
          { newTenantID: val.tenantID },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            withCredentials: true,
          }
        )
        .then((res: any) => {
          if (
            res &&
            res.data &&
            res.data.Status === 200 &&
            //@ts-ignore
            !val.mfaEnabled
          ) {
            window.location.href = "/";
          } else if (
            res &&
            res.data &&
            res.data.Status === 200 &&
            //@ts-ignore
            val.mfaEnabled
          ) {
            setMfa(true);
            //@ts-ignore
            setTenant(val);
          } else {
            console.log("Failed to switch tenant, please try again. Error: ");
          }
        })
        .catch((FEerror: any) => {
          //@ts-ignore
          if (FEerror.response.status === 404) setExpiredResetLink(true);
        });
    }
  };

  const handleOtpNumberChanged = (newValue: any) => {
    const inputCode = newValue.value;

    if (inputCode.toString().length === 6) {
      setOtp({
        ...otp,
        code: inputCode,
        validLength: true,
      });
      handleSubmitCode({
        ...otp,
        code: inputCode,
        validLength: true,
      });
    } else {
      setOtp({ ...otp, code: inputCode });
    }
  };

  const handleResendOTP = () => {
    axios
      .post(
        "/auth/resendMfa",
        //@ts-ignore
        { mfaResend: true },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      )
      .then((res: any) => {
        if (res && res.status === 200) {
          setMfa(true);

          setOtp({
            code: undefined,
            failedOtp: false,
            validLength: false,
          });
        } else {
          console.log(
            "Failed to authenticate tenant, please try again. Error: "
          );
          setOtp({
            code: undefined,
            failedOtp: false,
            validLength: false,
          });
        }
      })
      .catch((err) => {
        console.log("Failed to authenticate tenant, please try again. Error: ");
        setOtp({
          ...otp,
          failedOtp: true,
        });
      });
  };

  const handleSubmitCode = (otpValue: OtpState) => {
    axios
      .post(
        "/auth/verifyMfa",
        //@ts-ignore
        { mfaCode: otpValue.code },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      )
      .then((res: any) => {
        console.log(res);
        if (res && res.status === 200) {
          window.location.href = "/";
        } else {
          console.log(
            "Failed to authenticate tenant, please try again. Error: "
          );
          setOtp({
            ...otpValue,
            failedOtp: true,
            validLength: false,
          });
        }
      })
      .catch((err) => {
        console.log("Failed to authenticate tenant, please try again. Error: ");
        setOtp({
          ...otpValue,
          failedOtp: true,
          validLength: false,
        });
      });
  };

  return (
    <WcPaper>
      <WcStack
        data-cy="loginComp"
        alignItems="center"
        height="500px"
        width="500px"
        mt={!isCapensis ? 0 : "10px"}
        mb={!isCapensis ? 20 : 0}
        id="LoginComposite"
      >
        <WcStack margin={10} direction={"row"}>
          <Box
            component="img"
            sx={{
              height: !isCapensis ? 90 : 60,
            }}
            //@ts-ignore
            alt={!isCapensis ? "VBSO logo" : "Capensis logo"}
            src={
              !isCapensis
                ? "https://storage.googleapis.com/capensis-pub/VBSO-Logo.png"
                : "https://storage.googleapis.com/capensis-pub/Capensis-Logo_Gradient.png"
            }
          />
        </WcStack>
        {!mfa && !multipleTenants && (
          <WcStack alignItems={"center"} padding={4}>
            <WcStack
              mb={4}
              spacing={8}
              justifyContent={"center"}
              alignItems="center"
              width="100%"
            >
              <WcTypography variant="h5">{t("login.loginNovus")}</WcTypography>
              <WcTypography variant="body2">
                {t("common.fillDetails")}
              </WcTypography>
            </WcStack>
            {failedLogin && (
              <WcTypography
                variant="body1"
                data-cy="failedLoginText"
                //@ts-ignore
                color={theme.appColors.error}
              >
                {t("login.invalidCredentials")}
              </WcTypography>
            )}
            <WcStack justifyContent={"center"} alignItems="center" width="100%">
              <LoginForm onLoginDetails={handleLoginDetails} ref={loginRef} />
            </WcStack>

            <WcStack spacing={2} mt={4}>
              <WcStack alignItems={"center"}>
                <WcButton
                  data-cy="loginButton"
                  color="primary"
                  variant="contained"
                  size="medium"
                  onClick={handleLoginClicked}
                >
                  {t("login.login")}
                </WcButton>
              </WcStack>
            </WcStack>
            <WcStack spacing={2} padding={2} mt={8}>
              <WcLink href="/forgotPassword">
                <WcTypography
                  variant="body2"
                  //@ts-ignore
                  color={theme.palette.primary.main}
                >
                  {t("login.forgotPassword")}
                </WcTypography>
              </WcLink>
            </WcStack>
          </WcStack>
        )}
        {mfa && tenant && (
          <WcStack alignItems={"center"} padding={4}>
            <WcStack
              m={4}
              spacing={8}
              justifyContent={"center"}
              alignItems="center"
              width="100%"
            >
              <WcTypography variant="body2">
                {t("login.fillinOtp")}
              </WcTypography>
              {otp.failedOtp && (
                <WcTypography
                  variant="body1"
                  data-cy="failedOtpText"
                  //@ts-ignore
                  color={theme.appColors.error}
                >
                  {t("login.invalidOtp")}
                </WcTypography>
              )}
            </WcStack>
            <WcStack
              justifyContent={"center"}
              alignItems="center"
              width="100%"
              spacing={2}
              direction={"row"}
              mt={8}
            >
              {otp.validLength ? (
                <Box sx={{ width: "100%" }}>
                  <WcLinearProgress color="secondary" />
                </Box>
              ) : (
                !otp.failedOtp && (
                  <WcMaskedTextfield
                    value={otp.code}
                    sx={{
                      "& .MuiInputBase-input": {
                        letterSpacing: "15px",
                        textAlign: "center",
                        fontSize: "22px",
                        paddingY: 5,
                      },
                    }}
                    hiddenLabel
                    disabled={otp.validLength}
                    onChange={(value) => {
                      handleOtpNumberChanged(value.target);
                    }}
                    autoComplete="off"
                    variant="outlined"
                    mask={{
                      mask: "000000",
                      lazy: false,
                    }}
                  />
                )
              )}

              {otp.failedOtp && (
                <WcButton
                  data-cy="loginButton"
                  color="primary"
                  variant="contained"
                  size="medium"
                  onClick={handleResendOTP}
                >
                  {t("login.resendOTP")}
                </WcButton>
              )}
            </WcStack>
          </WcStack>
        )}

        {!mfa &&
          multipleTenants &&
          tenantList &&
          tenantList.length > 0 &&
          tenant && (
            <WcStack alignItems={"center"} padding={4}>
              <WcStack
                m={4}
                spacing={8}
                justifyContent={"center"}
                alignItems="center"
                width="100%"
              >
                <WcTypography variant="body2">
                  {t("login.selectTenant")}
                </WcTypography>
              </WcStack>

              <WcStack
                justifyContent={"center"}
                alignItems="center"
                width="100%"
                mt={8}
              >
                <TenantAutoComplete
                  width={300}
                  inputParam={tenant}
                  tenants={tenantList}
                  onValueChanged={handleTenantSelected}
                />
              </WcStack>
            </WcStack>
          )}
      </WcStack>
    </WcPaper>
  );
};

export default LoginComposite;
